import React, { useMemo, useState } from "react";
import { formatCurrency } from "../../helpers/paymentsHelper";

const ToggleTable = ({ data }) => {
  const getByPath = (obj, path) =>
    path.split('.').reduce((acc, key) => (acc?.[key] ?? undefined), obj);

  const cmp = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    const aNum = Number(a);
    const bNum = Number(b);
    const bothNumbers = !Number.isNaN(aNum) && !Number.isNaN(bNum);
    if (bothNumbers) return aNum - bNum;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  };

  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [searchQuery, setSearchQuery] = useState("");

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const companies = data.map(([name, totalTrips]) => ({
    name,
    totalTrips,
  }));

  const normalizeId = (val) => Array.isArray(val) ? val[0] : val;

  const driverMap = new Map();

  data.forEach(([companyName, _totalTrips, drivers]) => {
    drivers.forEach((driver) => {
      const id = normalizeId(driver.user_id);

      const existing = driverMap.get(id) || {
        id,
        name: driver.user_name,
        vehicle: driver.vehicle_reg,
        total_trips: 0,
        payout: driver.payout ?? 0,
        data: {},
      };

      existing.data[companyName] = {
        trips: driver.trip_count || 0,
        pay: driver.driver_pay || 0,
      };

      existing.total_trips += driver.trip_count || 0;

      if (driver.payout != null) existing.payout = driver.payout;

      if (!existing.vehicle && driver.vehicle_reg) existing.vehicle = driver.vehicle_reg;
      if (!existing.name && driver.user_name) existing.name = driver.user_name;

      driverMap.set(id, existing);
    });
  });

  const drivers = Array.from(driverMap.values());
  const query = (searchQuery || "").toLowerCase();

  const filteredDrivers = drivers.filter((driver) => {
    const name = (driver?.name || "").toLowerCase();
    const vehicle = (driver?.vehicle || "").toLowerCase();
    return name.includes(query) || vehicle.includes(query);
  });

  const sortedDrivers = useMemo(() => {
    if (!sortConfig.key) return filteredDrivers;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filteredDrivers].sort((a, b) => dir * cmp(getByPath(a, sortConfig.key), getByPath(b, sortConfig.key)));
  }, [filteredDrivers, sortConfig]);

  return (
    <div className="w-full h-full">
      <div className="px-1" style={{ maxWidth: "220px" }}>
        <div className="relative flex items-center">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ paddingTop: "0.44rem", paddingBottom: "0.44rem" }}
            className="w-full pl-4 pr-10 text-gray-800 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 placeholder-gray-400 placeholder-opacity-100 font-light text-sm"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          </div>
        </div>
      </div>
      <div
        className="overflow-y-auto w-full scrollbar"
        style={{ maxHeight: "600px" }}
      >
        <table className="border-collapse w-full h-10/12 overflow-y-auto">
          <thead>
            <tr className="bg-white sticky">
              <th
                className="border-b p-2 cursor-pointer text-left sticky left-0 bg-white z-10"
                onClick={() => handleSort("vehicle")}
              >
                Number Plate{" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1"
                >
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === "vehicle" &&
                      sortConfig.direction === "asc"
                        ? "#745D96"
                        : "#9C9C9C"
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === "vehicle" &&
                      sortConfig.direction === "desc"
                        ? "#745D96"
                        : "#9C9C9C"
                    }
                  />
                </svg>
              </th>
              <th
                className="border-b p-2 cursor-pointer text-left sticky bg-white z-10"
                style={{ left: "105px" }}
                onClick={() => handleSort("name")}
              >
                Driver{" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1"
                >
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === "name" &&
                      sortConfig.direction === "asc"
                        ? "#745D96"
                        : "#9C9C9C"
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === "name" &&
                      sortConfig.direction === "desc"
                        ? "#745D96"
                        : "#9C9C9C"
                    }
                  />
                </svg>
              </th>
              <th
                className="border-b p-2 cursor-pointer text-left"
                onClick={() => handleSort("payout")}
              >
                Payout{" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1"
                >
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === "payout" &&
                      sortConfig.direction === "asc"
                        ? "#745D96"
                        : "#9C9C9C"
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === "payout" &&
                      sortConfig.direction === "desc"
                        ? "#745D96"
                        : "#9C9C9C"
                    }
                  />
                </svg>
              </th>
              <th
                className="border-b p-2 cursor-pointer text-right"
                onClick={() => handleSort("total_trips")}
              >
                Total Trips{" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1"
                >
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === "total_trips" &&
                      sortConfig.direction === "asc"
                        ? "#745D96"
                        : "#9C9C9C"
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === "total_trips" &&
                      sortConfig.direction === "desc"
                        ? "#745D96"
                        : "#9C9C9C"
                    }
                  />
                </svg>
              </th>

              {companies.map((company) => (
                <React.Fragment key={company.name}>
                  <th
                    className="border-b p-2 cursor-pointer text-left"
                    onClick={() => handleSort(`data.${company.name}.trips`)}
                  >
                    {company.name} Trips
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="16"
                      height="32"
                      className="inline-block ml-1"
                    >
                      <path
                        d="M12 5l-5 5h10l-5-5z"
                        fill={
                          sortConfig.key === `data.${company.name}.trips` &&
                          sortConfig.direction === "asc"
                            ? "#745D96"
                            : "#9C9C9C"
                        }
                      />

                      <path
                        d="M12 19l5-5H7l5 5z"
                        fill={
                          sortConfig.key === `data.${company.name}.trips` &&
                          sortConfig.direction === "desc"
                            ? "#745D96"
                            : "#9C9C9C"
                        }
                      />
                    </svg>
                  </th>
                  <th
                    className="border-b p-2 cursor-pointer text-left"
                    onClick={() => handleSort(`data.${company.name}.pay`)}
                    title="Fare total before adjustments (bonus, deduction, etc.)"
                  >
                    {company.name} Pay
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="16"
                      height="32"
                      className="inline-block ml-1"
                    >
                      <path
                        d="M12 5l-5 5h10l-5-5z"
                        fill={
                          sortConfig.key === `data.${company.name}.pay` &&
                          sortConfig.direction === "asc"
                            ? "#745D96"
                            : "#9C9C9C"
                        }
                      />

                      <path
                        d="M12 19l5-5H7l5 5z"
                        fill={
                          sortConfig.key === `data.${company.name}.pay` &&
                          sortConfig.direction === "desc"
                            ? "#745D96"
                            : "#9C9C9C"
                        }
                      />
                    </svg>
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedDrivers.map((driver, idx) => (
              <tr
                className="border-t border-b p-2 cursor-pointer bg-white"
                key={idx}
              >
                <td className="border-t border-b p-2 text-left left-0 sticky bg-white z-10">
                  {driver.vehicle}
                </td>
                <td
                  className="border-t border-b p-2 text-left sticky bg-white z-10"
                  style={{ left: "105px" }}
                >
                  {driver.name}
                </td>
                <td className="border-t border-b p-2 text-left">
                  {formatCurrency(driver.payout)}
                </td>
                <td className="border-t border-b p-2 text-left">
                  {driver.total_trips}
                </td>

                {companies.map((company) => (
                  <React.Fragment key={company.name}>
                    <td className="border-t border-b p-2 text-left">
                      {driver.data[company.name]?.trips || 0}
                    </td>
                    <td className="border-t border-b p-2 text-left">
                      {formatCurrency(driver.data[company.name]?.pay)}
                    </td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ToggleTable;
